import { Observation } from "@/resources/types";

/**
 * Helper function to sort observations by creation date or use array order
 */
const getSortedObservations = (observations: Observation[]): Observation[] => {
  const allHaveCreatedField = observations.every(obs => obs.created);

  if (allHaveCreatedField) {
    return [...observations]
      .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  }

  return observations;
};

/**
 * Helper function to check if observation has valid hypercapnicRespiratoryFailure value
 */
const hasValidHypercapnicValue = (obs: Observation): boolean => {
  return obs.hypercapnicRespiratoryFailure !== undefined &&
    obs.hypercapnicRespiratoryFailure !== null &&
    typeof obs.hypercapnicRespiratoryFailure === "boolean";
};

/**
 * Helper function to check if observation has valid o2Flow value
 */
const hasValidO2FlowValue = (obs: Observation): boolean => {
  return obs.o2Flow !== undefined &&
    obs.o2Flow !== null &&
    typeof obs.o2Flow === "number";
};

/**
 * Helper function to find the last recorded values from sorted observations
 */
const findLastRecordedValues = (
  sortedObservations: Observation[]
): { hypercapnicRespiratoryFailure: boolean | null; o2FlowAboveZero: boolean | null } => {
  let hypercapnicRespiratoryFailure: boolean | null = null;
  let o2FlowAboveZero: boolean | null = null;

  // Search from end (most recent) to start (oldest)
  for (let i = sortedObservations.length - 1; i >= 0; i--) {
    const obs = sortedObservations[i];

    // Check for hypercapnicRespiratoryFailure if we haven't found it yet
    if (hypercapnicRespiratoryFailure === null && hasValidHypercapnicValue(obs)) {
      hypercapnicRespiratoryFailure = obs.hypercapnicRespiratoryFailure!;
    }

    // Check for o2Flow if we haven't found it yet
    if (o2FlowAboveZero === null && hasValidO2FlowValue(obs)) {
      o2FlowAboveZero = obs.o2Flow! > 0;
    }

    // If we've found both values, we can break early
    if (hypercapnicRespiratoryFailure !== null && o2FlowAboveZero !== null) {
      break;
    }
  }

  return { hypercapnicRespiratoryFailure, o2FlowAboveZero };
};

/**
 * Efficiently finds the most recent recorded values for both hypercapnicRespiratoryFailure and o2Flow
 * in a single iteration through the observations array.
 * Only considers non-invalid observations. Falls back to array index order if observations don't have created timestamps.
 *
 * @param observations - Array of observations
 * @returns Object containing both values: { hypercapnicRespiratoryFailure: boolean, o2FlowAboveZero: boolean }
 */
export const getLastRecordedObservationValues = (
  observations?: Observation[]
): { hypercapnicRespiratoryFailure: boolean; o2FlowAboveZero: boolean } => {
  if (!observations?.length) {
    return { hypercapnicRespiratoryFailure: false, o2FlowAboveZero: false };
  }

  const validObservations = observations.filter((obs) => !obs.invalid);
  if (!validObservations.length) {
    return { hypercapnicRespiratoryFailure: false, o2FlowAboveZero: false };
  }

  const sortedObservations = getSortedObservations(validObservations);
  const values = findLastRecordedValues(sortedObservations);

  return {
    hypercapnicRespiratoryFailure: values.hypercapnicRespiratoryFailure ?? false,
    o2FlowAboveZero: values.o2FlowAboveZero ?? false
  };
};
